import { Directive } from '@angular/core';
import {
  deepClone,
  downloadFile,
  GridStateV2,
  GridUtilsV2,
  StorageV2Service,
  StoreV2,
  ToastV2Service,
} from '@schenck/rotec-components';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { INITIAL_GRID_PARAMS, INITIAL_GRID_STATE } from '../utils/constants/grid.const';
import { GridData, GridParams } from '../utils/models/grid-config.model';
import { catchError, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { getTimestamp, showSuccessToast } from 'src/utils';
import { SpinnerService } from './spinner-service/spinner.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MachineResponseError } from 'src/utils/models/machine.model';

interface BaseApi<T> {
  create$: (entity: Partial<T>) => Observable<T>;
  delete$: (id: string) => Observable<boolean>;
  get$: () => Observable<T[]>;
  update$?: (entity: Partial<T>) => Observable<T>;
}

type ServiceKey = 'User' | 'Machine' | 'Tenant';

@Directive()
export class BaseService<Value> extends StoreV2<GridData<Value>> {
  protected _results$ = new BehaviorSubject<Value[]>(null);
  protected _gridParams$ = new BehaviorSubject<GridParams>(null);
  protected _loading$ = new BehaviorSubject<boolean>(false);

  protected filterMapFn: Partial<
    Record<string, (resultProp: any, filterValue: any, result: Value) => boolean>
  > = {};

  public get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  public get results$(): Observable<Value[]> {
    return this._results$.asObservable();
  }

  public get results(): Value[] {
    return this._results$.getValue();
  }

  constructor(
    protected storageService: StorageV2Service,
    protected toastService: ToastV2Service,
    protected baseApi: BaseApi<Value>,
    protected serviceKey: ServiceKey,
    protected spinnerService: SpinnerService
  ) {
    super(deepClone(INITIAL_GRID_STATE), serviceKey, {
      name: `${serviceKey}AccountService`,
      storageService,
    });
  }

  public initGridState(): void {
    this.subsribeToGridParamsChanges();
    this._gridParams$.next({
      ...INITIAL_GRID_PARAMS,
      page: this.state.page,
      pageSize: this.state.pageSize,
      sort: this.state.sort,
    });
  }

  public updateGridState(params: GridParams): void {
    const updatedParams = {
      ...this._gridParams$.value,
      ...params,
    };

    switch (params?.type) {
      case 'sort': {
        this.patchState(`sorted ${this.serviceKey.toLowerCase()} grid`, {
          results: this.getSortedResults(updatedParams),
          ...updatedParams,
        });
        break;
      }
      case 'deleteFilter':
      case 'filter': {
        this.patchState(`updated ${this.serviceKey.toLowerCase()} grid`, updatedParams);
        this._gridParams$.next({ ...updatedParams, page: 0 });
        break;
      }
      case 'pageChanged': {
        this.patchState(`updated ${this.serviceKey.toLowerCase()} grid`, updatedParams);
        this._gridParams$.next(updatedParams);
        break;
      }
    }
  }

  public delete$(id: string): void {
    this.setLoading(true);
    this.baseApi
      .delete$(id)
      .pipe(
        take(1),
        catchError((err) => {
          this.setLoading(false);

          throw Error(err);
        })
      )
      .subscribe(() => {
        showSuccessToast(this.serviceKey.toLowerCase(), 'deleted', this.toastService);
        this.loadGridData();
      });
  }

  public create$(entity: Partial<Value>): Observable<Value> {
    this.spinnerService.setSpinnerState(true);

    return this.baseApi.create$(entity).pipe(
      tap(() => {
        showSuccessToast(this.serviceKey.toLowerCase(), 'created', this.toastService);
        this.loadGridData();
      }),
      catchError(this.catchError.bind(this)),
      finalize(() => this.spinnerService.setSpinnerState(false))
    );
  }

  public update$(entity: Partial<Value>): Observable<Value> {
    this.spinnerService.setSpinnerState(true);

    return this.baseApi.update$(entity).pipe(
      tap(() => {
        showSuccessToast(this.serviceKey.toLowerCase(), 'updated', this.toastService);
        this.loadGridData();
      }),
      catchError(this.catchError.bind(this)),
      finalize(() => this.spinnerService.setSpinnerState(false))
    );
  }

  public handleCSVExport(response$: Observable<Blob>, type: string): void {
    response$
      .pipe(
        take(1),
        catchError(({ message }: HttpErrorResponse) => {
          this.toastService.open({ text: message, type: 'error' });

          throw Error(message);
        })
      )
      .subscribe((response) =>
        downloadFile(response, `acm_${type}_${getTimestamp()}`, 'csv', 'text/csv;charset=utf-8')
      );
  }

  protected loadGridData(params?: GridParams): void {
    this._gridParams$.next(params || this._gridParams$.value);
  }

  protected getSortedResults(
    params: GridParams = this._gridParams$.value,
    results?: Value[]
  ): Value[] {
    const sortedResults = GridUtilsV2.getResults(results || this.results, {
      ...params,
      filterFnMap: this.filterMapFn,
      resultComparator: GridUtilsV2.defaultResultComparator,
    })?.results;

    return sortedResults || [];
  }

  protected getResults(params?: GridParams): Partial<GridStateV2<Value>> {
    const filteredResults = GridUtilsV2.filterResults(
      this.results,
      params?.filter || this.state.filter,
      this.filterMapFn
    );

    return {
      results: this.getSortedResults(params, filteredResults),
      total: filteredResults?.length,
    };
  }

  protected setLoading(loading: boolean): void {
    this._loading$.next(loading);
  }

  // Basic subscribtion to params changes. Can be overwritten in child services
  protected subsribeToGridParamsChanges(): void {
    this._gridParams$
      .pipe(
        filter((params) => !!params),
        tap(() => this.setLoading(true)),
        switchMap(() => this.baseApi.get$()),
        catchError(() => {
          this._results$.next([]);

          return of([]);
        })
      )
      .subscribe((results) => {
        this.setLoading(false);
        this._results$.next(<Value[]>results);
        this.patchState(`loaded ${this.serviceKey.toLowerCase()} grid results`, this.getResults());
      });
  }

  private catchError(error: HttpErrorResponse): Observable<any> {
    // General error toast.
    // will only be shown if the error has a message
    if (error?.error?.message) {
      this.toastService.open({ text: error?.error?.message, type: 'error' });
    }

    throw new MachineResponseError(error?.error?.message, error?.error?.params);
  }
}
